import React from 'react';
import { Loan } from '../../types/dashboard';
import { X, AlertCircle, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
interface StageDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  stageName: string;
  stageColor: string;
  loans: Loan[];
}
export function StageDetailModal({
  isOpen,
  onClose,
  stageName,
  stageColor,
  loans
}: StageDetailModalProps) {
  if (!isOpen) return null;
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getStatusBadge = (status: Loan['status']) => {
    switch (status) {
      case 'On Track':
        return <span className="inline-flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md text-xs font-medium" data-id="element-257">
            <CheckCircle className="w-3 h-3" data-id="element-258" /> On Track
          </span>;
      case 'At Risk':
        return <span className="inline-flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2 py-1 rounded-md text-xs font-medium" data-id="element-259">
            <AlertTriangle className="w-3 h-3" data-id="element-260" /> At Risk
          </span>;
      case 'Delayed':
        return <span className="inline-flex items-center gap-1.5 text-rose-600 bg-rose-50 px-2 py-1 rounded-md text-xs font-medium" data-id="element-261">
            <AlertCircle className="w-3 h-3" data-id="element-262" /> Delayed
          </span>;
      case 'Clear to Close':
        return <span className="inline-flex items-center gap-1.5 text-blue-600 bg-blue-50 px-2 py-1 rounded-md text-xs font-medium" data-id="element-263">
            <CheckCircle className="w-3 h-3" data-id="element-264" /> Clear to Close
          </span>;
      default:
        return <span className="inline-flex items-center gap-1.5 text-slate-600 bg-slate-50 px-2 py-1 rounded-md text-xs font-medium" data-id="element-265">
            <Clock className="w-3 h-3" data-id="element-266" /> {status}
          </span>;
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-267">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-268">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-center justify-between" data-id="element-269">
          <div className="flex items-center gap-3" data-id="element-270">
            <div className="w-3 h-10 rounded-full" style={{
            backgroundColor: stageColor
          }} data-id="element-271" />
            <div data-id="element-272">
              <h2 className="text-xl font-bold text-slate-800" data-id="element-273">
                {stageName} Stage
              </h2>
              <p className="text-sm text-slate-500" data-id="element-274">
                {loans.length} loan{loans.length !== 1 ? 's' : ''} in this stage
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-slate-700" aria-label="Close modal" data-id="element-275">
            <X className="w-5 h-5" data-id="element-276" />
          </button>
        </div>

        {/* Table Container */}
        <div className="flex-1 overflow-auto" data-id="element-277">
          {loans.length === 0 ? <div className="p-12 text-center text-slate-500" data-id="element-278">
              <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-279" />
              <p className="text-lg font-medium" data-id="element-280">No loans in this stage</p>
              <p className="text-sm" data-id="element-281">
                Loans will appear here when they enter the {stageName} stage.
              </p>
            </div> : <table className="w-full text-sm text-left" data-id="element-282">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200 sticky top-0" data-id="element-283">
                <tr data-id="element-284">
                  <th className="px-5 py-3" data-id="element-285">Loan ID</th>
                  <th className="px-5 py-3" data-id="element-286">Borrower Name</th>
                  <th className="px-5 py-3" data-id="element-287">Loan Amount</th>
                  <th className="px-5 py-3" data-id="element-288">Status</th>
                  <th className="px-5 py-3" data-id="element-289">Days in Stage</th>
                  <th className="px-5 py-3" data-id="element-290">Closing Date</th>
                  <th className="px-5 py-3" data-id="element-291">Conditions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-292">
                {loans.map(loan => <tr key={loan.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-293">
                    <td className="px-5 py-4 font-mono text-slate-600 font-medium" data-id="element-294">
                      {loan.id}
                    </td>
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-295">
                      {loan.borrowerName}
                    </td>
                    <td className="px-5 py-4 text-slate-700 font-semibold" data-id="element-296">
                      {formatCurrency(loan.loanAmount)}
                    </td>
                    <td className="px-5 py-4" data-id="element-297">{getStatusBadge(loan.status)}</td>
                    <td className="px-5 py-4" data-id="element-298">
                      <div className={`flex items-center gap-1.5 ${loan.daysInStage > 7 ? 'text-rose-600 font-bold' : loan.daysInStage > 4 ? 'text-amber-600' : 'text-slate-600'}`} data-id="element-299">
                        <Clock className="w-4 h-4" data-id="element-300" />
                        {loan.daysInStage} days
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-600" data-id="element-301">
                      {formatDate(loan.closingDate)}
                    </td>
                    <td className="px-5 py-4" data-id="element-302">
                      {loan.pendingConditions > 0 ? <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${loan.pendingConditions > 3 ? 'bg-rose-100 text-rose-700' : loan.pendingConditions > 1 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-303">
                          {loan.pendingConditions} pending
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" data-id="element-304">
                          All cleared
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-305">
          <p className="text-sm text-slate-500" data-id="element-306">
            Total Value:{' '}
            <span className="font-semibold text-slate-700" data-id="element-307">
              {formatCurrency(loans.reduce((sum, loan) => sum + loan.loanAmount, 0))}
            </span>
          </p>
          <button onClick={onClose} className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium" data-id="element-308">
            Close
          </button>
        </div>
      </div>
    </div>;
}